// LieToMeScreen.jsx
import React, { useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";

const DEFAULT_OBJECTS = [
    { value: 0, icon: "💍", names: { es: "Anillo", en: "Ring", fr: "Bague" } },
    { value: 3, icon: "🪙", names: { es: "Moneda", en: "Coin", fr: "Pièce" } },
    { value: 5, icon: "🔑", names: { es: "Llave", en: "Key", fr: "Clé" } },
    { value: 6, icon: "⌚", names: { es: "Reloj", en: "Watch", fr: "Montre" } },
];

const QUESTIONS = [
    { weight: 1, values: [3, 5] },
    { weight: 2, values: [3, 6] },
    { weight: 4, values: [5, 6] },
];

const TEXTS = {
    es: {
        subtitle: "Herramienta para practicar Lie to Me de Leo Boudreau.",
        reset: "Nuevo ensayo",
        spectators: "Espectadores",
        spectator: "Espectador",
        hidden: "Elección oculta",
        questions: "Rondas de preguntas",
        instructions:
            "Pulsa cada ronda para recibir las respuestas de todos los espectadores.",
        question: "Ronda",
        asks: ["¿Moneda o llave?", "¿Reloj o moneda?", "¿Llave o reloj?"],
        answer: "Respuesta",
        yes: "Sí",
        no: "No",
        reveal: "Mostrar solución",
        object: "Objeto",
        behavior: "Comportamiento",
        truth: "Verdad",
        lie: "Mentira",
        total: "Total",
        cheatSheet: "Chuleta del código",
        weights: "Pesos de las rondas",
        code: "Código de objetos",
        result: "Resultado",
        objects: "Objetos",
        customizeObjects:
            "Personaliza los nombres e iconos utilizados en el ensayo.",
        restoreObjects: "Restaurar originales",
        icon: "Icono",
    },
    en: {
        subtitle: "Practice tool for Leo Boudreau’s Lie to Me.",
        reset: "New rehearsal",
        spectators: "Spectators",
        spectator: "Spectator",
        hidden: "Hidden choice",
        questions: "Question rounds",
        instructions: "Press each round to receive every spectator’s answer.",
        question: "Round",
        asks: ["Coin or key?", "Watch or coin?", "Key or watch?"],
        answer: "Answer",
        yes: "Yes",
        no: "No",
        reveal: "Show solution",
        object: "Object",
        behavior: "Behavior",
        truth: "Truth",
        lie: "Lie",
        total: "Total",
        cheatSheet: "Code cheat sheet",
        weights: "Round weights",
        code: "Object code",
        result: "Result",
        objects: "Objects",
        customizeObjects:
            "Customize the names and icons used in the rehearsal.",
        restoreObjects: "Restore defaults",
        icon: "Icon",
    },
    fr: {
        subtitle: "Outil pour pratiquer Lie to Me de Leo Boudreau.",
        reset: "Nouvel essai",
        spectators: "Spectateurs",
        spectator: "Spectateur",
        hidden: "Choix caché",
        questions: "Tours de questions",
        instructions:
            "Appuyez sur chaque tour pour obtenir toutes les réponses.",
        question: "Tour",
        asks: ["Pièce ou clé ?", "Montre ou pièce ?", "Clé ou montre ?"],
        answer: "Réponse",
        yes: "Oui",
        no: "Non",
        reveal: "Afficher la solution",
        object: "Objet",
        behavior: "Comportement",
        truth: "Vérité",
        lie: "Mensonge",
        total: "Total",
        cheatSheet: "Aide-mémoire du code",
        weights: "Valeurs des tours",
        code: "Code des objets",
        result: "Résultat",
        objects: "Objets",
        customizeObjects: "Personnalisez les noms et les icônes utilisés.",
        restoreObjects: "Restaurer",
        icon: "Icône",
    },
};

const createSpectators = (count, objects) =>
    Array.from({ length: count }, () => ({
        object: objects[Math.floor(Math.random() * objects.length)],
        lies: Math.random() < 0.5,
    }));

const LieToMeScreen = () => {
    const [objects, setObjects] = useState(DEFAULT_OBJECTS);
    const [language, setLanguage] = useState("es");
    const [spectatorCount, setSpectatorCount] = useState(1);
    const [spectators, setSpectators] = useState(() =>
        createSpectators(1, DEFAULT_OBJECTS),
    );
    const [rounds, setRounds] = useState([false, false, false]);
    const [revealed, setRevealed] = useState(false);
    const text = TEXTS[language];

    const reset = (count = spectatorCount, selectedObjects = objects) => {
        setSpectators(createSpectators(count, selectedObjects));
        setRounds([false, false, false]);
        setRevealed(false);
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Stack spacing={{ xs: 1, sm: 2.5 }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.25}
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography
                            component="h1"
                            sx={{
                                fontSize: { xs: 30, sm: 44 },
                                fontWeight: 950,
                                lineHeight: 1,
                                letterSpacing: -2,
                            }}
                        >
                            LIE{" "}
                            <Box component="span" sx={{ color: "error.main" }}>
                                TO ME
                            </Box>
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: { xs: 0.5, sm: 1 },
                                fontSize: { xs: 12, sm: "0.875rem" },
                            }}
                        >
                            {text.subtitle}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="stretch">
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={language}
                            aria-label="Language"
                            onChange={(_, value) => value && setLanguage(value)}
                        >
                            <ToggleButton value="es">ES</ToggleButton>
                            <ToggleButton value="en">EN</ToggleButton>
                            <ToggleButton value="fr">FR</ToggleButton>
                        </ToggleButtonGroup>

                        <TextField
                            select
                            size="small"
                            label={text.spectators}
                            value={spectatorCount}
                            onChange={(event) => {
                                const count = Number(event.target.value);
                                setSpectatorCount(count);
                                reset(count);
                            }}
                            sx={{ minWidth: 120 }}
                        >
                            {Array.from(
                                { length: 12 },
                                (_, index) => index + 1,
                            ).map((count) => (
                                <MenuItem key={count} value={count}>
                                    {count}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Button
                            variant="contained"
                            onClick={() => reset()}
                            sx={{
                                px: { xs: 1, sm: 2 },
                                fontSize: { xs: 11, sm: "0.875rem" },
                            }}
                        >
                            {text.reset}
                        </Button>
                    </Stack>
                </Stack>

                <Divider />

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            sm: "repeat(3, minmax(0, 1fr))",
                            lg: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: { xs: 0.75, sm: 2 },
                    }}
                >
                    {spectators.map((spectator, spectatorIndex) => {
                        const answers = QUESTIONS.map(
                            ({ values }) =>
                                values.includes(spectator.object.value) !==
                                spectator.lies,
                        );
                        const total = answers.reduce(
                            (sum, answer, index) =>
                                sum + (answer ? QUESTIONS[index].weight : 0),
                            0,
                        );

                        return (
                            <Paper
                                key={spectatorIndex}
                                variant="outlined"
                                sx={{
                                    p: { xs: 0.75, sm: 2 },
                                    minWidth: 0,
                                    borderRadius: 2,
                                }}
                            >
                                <Typography
                                    align="center"
                                    variant="subtitle2"
                                    sx={{ mb: 1, fontWeight: 800 }}
                                >
                                    {text.spectator} {spectatorIndex + 1}
                                </Typography>

                                <Box
                                    sx={{
                                        height: { xs: 82, sm: 110 },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: 2,
                                        bgcolor: revealed
                                            ? "action.hover"
                                            : "error.dark",
                                        color: revealed
                                            ? "text.primary"
                                            : "common.white",
                                    }}
                                >
                                    {revealed ? (
                                        <Stack
                                            alignItems="center"
                                            spacing={0.25}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: {
                                                        xs: 30,
                                                        sm: 42,
                                                    },
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {spectator.object.icon}
                                            </Typography>
                                            <Typography
                                                sx={{ fontWeight: 900 }}
                                            >
                                                {spectator.object.name ??
                                                    spectator.object.names[
                                                        language
                                                    ]}
                                            </Typography>
                                            <Typography
                                                sx={{ fontWeight: 700 }}
                                            >
                                                {spectator.lies
                                                    ? text.lie
                                                    : text.truth}
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 30, sm: 42 },
                                                fontWeight: 950,
                                            }}
                                        >
                                            ?
                                        </Typography>
                                    )}
                                </Box>

                                <Stack spacing={0.65} sx={{ mt: 1 }}>
                                    {answers.map((answer, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                minHeight: 34,
                                                px: 1,
                                                py: 0.5,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 1,
                                                border: 1,
                                                borderColor: "divider",
                                                borderRadius: 1,
                                                bgcolor: rounds[index]
                                                    ? "action.hover"
                                                    : "transparent",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {text.question} {index + 1}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 800,
                                                    color: rounds[index]
                                                        ? answer
                                                            ? "success.main"
                                                            : "error.main"
                                                        : "text.disabled",
                                                }}
                                            >
                                                {rounds[index]
                                                    ? answer
                                                        ? text.yes
                                                        : text.no
                                                    : "—"}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>

                                {revealed && (
                                    <Stack
                                        spacing={0.35}
                                        sx={{
                                            mt: 1.25,
                                            pt: 1.25,
                                            borderTop: 1,
                                            borderColor: "divider",
                                        }}
                                    >
                                        <Typography variant="body2">
                                            <b>{text.object}:</b>{" "}
                                            {spectator.object.name ??
                                                spectator.object.names[
                                                    language
                                                ]}
                                            ({spectator.object.value})
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>{text.behavior}:</b>{" "}
                                            {spectator.lies
                                                ? text.lie
                                                : text.truth}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>{text.total}:</b> {total}
                                        </Typography>
                                    </Stack>
                                )}
                            </Paper>
                        );
                    })}
                </Box>

                <Paper
                    variant="outlined"
                    sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2 }}
                >
                    <Stack spacing={1.5}>
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700 }}
                            >
                                {text.questions}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {text.instructions}
                            </Typography>
                        </Box>

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                        >
                            {QUESTIONS.map((question, index) => (
                                <Button
                                    key={question.weight}
                                    variant="outlined"
                                    disabled={rounds[index]}
                                    onClick={() =>
                                        setRounds((previous) =>
                                            previous.map((value, roundIndex) =>
                                                roundIndex === index
                                                    ? true
                                                    : value,
                                            ),
                                        )
                                    }
                                    sx={{ flex: 1 }}
                                >
                                    {text.question} {index + 1} ·{" "}
                                    {text.asks[index]} · {question.weight} pt
                                </Button>
                            ))}
                            <Button
                                variant="contained"
                                color="success"
                                disabled={!rounds.every(Boolean) || revealed}
                                onClick={() => setRevealed(true)}
                            >
                                {text.reveal}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
                <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: "8px !important",
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary
                        expandIcon={
                            <Box component="span" sx={{ fontSize: 20 }}>
                                ⌄
                            </Box>
                        }
                    >
                        <Typography sx={{ fontWeight: 800 }}>
                            {text.objects}
                        </Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 0 }}>
                        <Stack spacing={1.5}>
                            <Typography variant="body2" color="text.secondary">
                                {text.customizeObjects}
                            </Typography>

                            {objects.map((object, index) => (
                                <Stack
                                    key={object.value}
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                >
                                    <TextField
                                        size="small"
                                        label={text.icon}
                                        value={object.icon}
                                        onChange={(event) =>
                                            setObjects((previous) =>
                                                previous.map(
                                                    (item, itemIndex) =>
                                                        itemIndex === index
                                                            ? {
                                                                  ...item,
                                                                  icon: event
                                                                      .target
                                                                      .value,
                                                              }
                                                            : item,
                                                ),
                                            )
                                        }
                                        sx={{ width: { sm: 90 } }}
                                    />

                                    <TextField
                                        size="small"
                                        label={text.object}
                                        value={
                                            object.name ??
                                            object.names[language]
                                        }
                                        onChange={(event) =>
                                            setObjects((previous) =>
                                                previous.map(
                                                    (item, itemIndex) =>
                                                        itemIndex === index
                                                            ? {
                                                                  ...item,
                                                                  name: event
                                                                      .target
                                                                      .value,
                                                              }
                                                            : item,
                                                ),
                                            )
                                        }
                                        fullWidth
                                    />
                                </Stack>
                            ))}

                            <Box>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setObjects(DEFAULT_OBJECTS);
                                        reset(spectatorCount, DEFAULT_OBJECTS);
                                    }}
                                >
                                    {text.restoreObjects}
                                </Button>
                            </Box>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
                <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: "8px !important",
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary
                        expandIcon={
                            <Box component="span" sx={{ fontSize: 20 }}>
                                ⌄
                            </Box>
                        }
                    >
                        <Typography sx={{ fontWeight: 800 }}>
                            {text.cheatSheet}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        <Stack spacing={2}>
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 0.75, fontWeight: 800 }}
                                >
                                    {text.weights}
                                </Typography>
                                {QUESTIONS.map((question, index) => (
                                    <Typography
                                        key={question.weight}
                                        variant="body2"
                                    >
                                        {text.question} {index + 1}:{" "}
                                        {text.asks[index]} —{" "}
                                        <b>
                                            {text.yes} = {question.weight}
                                        </b>
                                        , {text.no} = 0
                                    </Typography>
                                ))}
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 0.75, fontWeight: 800 }}
                                >
                                    {text.code}
                                </Typography>
                                <Typography variant="body2">
                                    {objects
                                        .map(
                                            (object) =>
                                                `${object.icon} ${object.name ?? object.names[language]} = ${object.value}`,
                                        )
                                        .join(" · ")}
                                </Typography>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{text.result}</TableCell>
                                            <TableCell>{text.object}</TableCell>
                                            <TableCell>
                                                {text.behavior}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[0, 1, 2, 3, 4, 5, 6, 7].map(
                                            (result) => {
                                                const object = objects.find(
                                                    ({ value }) =>
                                                        value === result ||
                                                        7 - value === result,
                                                );
                                                const lies = !objects.some(
                                                    ({ value }) =>
                                                        value === result,
                                                );
                                                return (
                                                    <TableRow key={result}>
                                                        <TableCell>
                                                            <b>{result}</b>
                                                        </TableCell>
                                                        <TableCell>
                                                            {object.icon}{" "}
                                                            {
                                                                object.names[
                                                                    language
                                                                ]
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {lies
                                                                ? text.lie
                                                                : text.truth}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            },
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </Stack>
        </Box>
    );
};

export default LieToMeScreen;
